import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { Controller } from "react-hook-form";
import { useSearch } from "@/hooks/useSearch";
import Table, { TableDetails } from "@/components/Table";
import Button from "@/components/Button";
import SearchInput from "@/components/SearchInput";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import usePagination from "@/hooks/usePagination";
import {
  EventRegistrants,
  Variables,
  RegistrationStatus,
  StatusCounts,
  EventRegistrationAdditionalStatus
} from "../types";
import { EVENT_REGISTRANTS_QUERY, LATE_CANCEL_REGISTRANTS_QUERY, PAT_WORKSHEET_MUTATION } from "../../../queries";
import { useEventDetailQueryDocument } from "../../../useEventDetailQuery";
import { RegistrantRow, EventRegistrantNode } from "../types";
import { SortingState, Table as TableComponent } from "@tanstack/react-table";
import FinalizeScoresConfirmationModal from "./FinalizeScoresConfirmationModal";
import FinalizeScoresErrorDialog from "./FinalizeScoresErrorModal";
import UpdateStatusModal from "./UpdateStatusModal";
import { getColumns } from "./TableColumns";
import EmptyState from "@/components/EmptyState";
import { UPDATE_EVENT_SESSIONS_REGISTRATION_ATTENDANCES_MUTATION } from "../../attendance/queries";
import { PAT_FINALIZE_SCORES_MUTATION, PAT_STATUS_COUNTS_QUERY, PAT_UPSERT_SUBMISSION_MUTATION } from "../queries";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { EventOutletContext } from "../../_layout";
import getAge from "@/lib/ageHelper";
import pluralize from "@/lib/pluralize";
import Chip from "@/components/Chip";
import { Event } from "../../../types";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import EditScoreModal from "./EditScoreModal";
import Checkbox from "@/components/Checkbox";
import Banner from "@/components/Banner";
import Tooltip from "@/components/Tooltip";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";

interface RegistrantsTableProps {
  eventSlug: string;
  eventData?: Event;
  isSetupComplete: boolean;
  refreshKey?: number;
}
export type UpdateStatus = {
  status: string;
  note: string;
  id: string;
};

const mapRegistrantsData = (data: EventRegistrantNode[]) =>
  data.map(({ node: { sessionRegistrations, patSubmission, customFields, ...node } }) => ({
    ...node,
    score_18_1: patSubmission?.roundOne?.roundScore || "",
    meets_qualifying_score_18_1: patSubmission?.roundOne?.meetsQualifyingScore || false,
    score_18_2: patSubmission?.roundTwo?.roundScore || "",
    meets_qualifying_score_18_2: patSubmission?.roundTwo?.meetsQualifyingScore || false,
    score_total: patSubmission?.totalScore || null,
    score_target: patSubmission?.targetScore || null,
    attended: sessionRegistrations?.[0]?.attended,
    gender: customFields?.gender || null,
    age: getAge(customFields?.date_of_birth),
    status: patSubmission?.result || null,
    additionalStatus: patSubmission?.additionalStatus,
    note: patSubmission?.note,
    sessionRegistrationId: sessionRegistrations?.[0]?.id,
    date: patSubmission?.date || "",
    suspension: patSubmission?.suspension,
    document: patSubmission?.document || null,
    documentUrl: patSubmission?.document?.url || null,
    hasDocument: !!patSubmission?.document
  }));

export default function RegistrantsTable({ eventSlug, eventData, isSetupComplete, refreshKey }: RegistrantsTableProps) {
  const eventDetailQuery = useEventDetailQueryDocument();
  const { hasPermission } = useCapabilities();
  const { paginationVariables, renderFooter, resetPagination } = usePagination();
  const { setErrorMessage } = useOutletContext<EventOutletContext>();
  const { trackEvent } = useAmplitude();
  const searchField = "search";
  const { searchValue, control, reset } = useSearch(searchField);
  const [variables, setVariables] = useState<Variables>({ eventSlug });
  const filterActive = variables.search;
  const { isCompactLayout: isMobile } = useIsCompactLayout();

  const [finalizeScoresDialogOpen, setFinalizeScoresDialogOpen] = useState(false);
  const [finalizeScoresErrorDialogOpen, setFinalizeScoresErrorDialogOpen] = useState(false);
  const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
  const [targetEventRegistration, setTargetEventRegistration] = useState<RegistrantRow | null>(null);
  const [showEditScoreModal, setShowEditScoreModal] = useState(false);
  const [invalidScoresCount, setInvalidScoresCount] = useState(0);
  const [missingStatusCount, setMissingStatusCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<StatusCounts>({
    invalidCount: 0,
    passingCount: 0,
    qualifyingCount: 0,
    failingCount: 0,
    cheatingCount: 0,
    excessiveCount: 0,
    noCardCount: 0,
    noShowCount: 0,
    poorConductCount: 0,
    weatherEventCount: 0,
    withdrawalCount: 0,
    excusedCount: 0,
    medicalWithdrawalCount: 0,
    disqualifiedCount: 0,
    missingStatusCount: 0
  });
  const [invalidEventAttributesCount, setInvalidEventAttributesCount] = useState(0);
  const [finalizeScoresLoading, setFinalizeScoresLoading] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [hasWeatherEvents, setHasWeatherEvents] = useState(false);

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "participant",
      desc: false
    }
  ]);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const rowSelected = Object.keys(rowSelection).length;
  const [isSelectedOnly, setIsSelectedOnly] = useState(false);
  const { Menu, setMenu } = useTableRowMenu();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);

  const {
    data: lateCancelRegistrantsData,
    loading: lateCancelQueryLoading,
    refetch: refetchLateCancelRegistrants
  } = useQuery<{ lateCancelRegistrants: { edges: EventRegistrantNode[] } }>(LATE_CANCEL_REGISTRANTS_QUERY, {
    variables: { slug: eventSlug }
  });

  const {
    data: registrantsData,
    loading: queryLoading,
    refetch: refetchRegistrants
  } = useQuery<EventRegistrants>(EVENT_REGISTRANTS_QUERY, {
    variables: { ...variables, ...paginationVariables, status: RegistrationStatus.REGISTERED }
  });
  const [registrantsQuery, { data: allRegistrantsData, loading: allRegistrantsLoading }] =
    useLazyQuery<EventRegistrants>(EVENT_REGISTRANTS_QUERY, {
      variables: { eventSlug, status: RegistrationStatus.REGISTERED }
    });

  const [updateEventSessionsRegistrationAttendances] = useMutation(
    UPDATE_EVENT_SESSIONS_REGISTRATION_ATTENDANCES_MUTATION
  );
  const [patUpsertSubmission] = useMutation(PAT_UPSERT_SUBMISSION_MUTATION);
  const [patFinalizeScores] = useMutation(PAT_FINALIZE_SCORES_MUTATION);

  const [statusCountsQuery] = useLazyQuery(PAT_STATUS_COUNTS_QUERY, {
    variables: { eventId: eventData?.id }
  });

  const [generateWorksheetMutation] = useMutation(PAT_WORKSHEET_MUTATION);

  const isFinalized = !!eventData?.patEventAttributes.finalized;
  const canEdit = isFinalized ? hasPermission(PERMISSION.editPAT) : hasPermission(PERMISSION.managePAT);

  const getWeatherEvents = (registrations: RegistrantRow[]) => {
    return registrations.some((reg) => reg.additionalStatus === EventRegistrationAdditionalStatus.WEATHER_EVENT);
  };

  useEffect(() => {
    resetPagination();
    setVariables((prev) => ({
      ...(searchValue ? { ...prev, search: searchValue } : { eventSlug })
    }));
  }, [searchValue, eventSlug, resetPagination]);

  const [registrations, setRegistrations] = useState<RegistrantRow[]>([]);

  useEffect(() => {
    const registrants = mapRegistrantsData(
      (isSelectedOnly ? allRegistrantsData?.eventRegistrants.edges : registrantsData?.eventRegistrants.edges) || []
    );

    const lateCancelRegistrants = mapRegistrantsData(lateCancelRegistrantsData?.lateCancelRegistrants?.edges || []);

    const allRegistrations = [...registrants, ...lateCancelRegistrants];
    setRegistrations(allRegistrations);

    setHasWeatherEvents(getWeatherEvents(allRegistrations));
  }, [isSelectedOnly, registrantsData, allRegistrantsData, lateCancelRegistrantsData]);

  useEffect(() => {
    if (refreshKey === undefined) return;
    refetchRegistrants({ ...variables, ...paginationVariables, status: RegistrationStatus.REGISTERED });
    refetchLateCancelRegistrants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    if (rowSelected === 0 && isSelectedOnly) {
      setIsSelectedOnly(false);
    }
  }, [rowSelected, isSelectedOnly]);

  const hasRegistrations = registrations.length > 0;

  const validateEventAttributes = () => {
    const eventFields = [
      !eventData?.patEventAttributes?.hostProfessional?.id,
      !eventData?.patEventAttributes?.examiner?.id,
      !eventData?.patEventAttributes?.courseConditions,
      !eventData?.patEventAttributes?.yardageMale,
      !eventData?.patEventAttributes?.courseRatingMale,
      !eventData?.patEventAttributes?.yardageMaleFiftyPlus,
      !eventData?.patEventAttributes?.yardageFemale,
      !eventData?.patEventAttributes?.courseRatingFemale,
      !eventData?.patEventAttributes?.yardageFemaleFiftyPlus,
      !eventData?.patEventAttributes?.targetScoreMale,
      !eventData?.patEventAttributes?.targetScoreFemale
    ];

    return eventFields.reduce((acc, isValid) => acc + (isValid ? 1 : 0), 0);
  };

  const handleFinalizeScores = async () => {
    try {
      const { data } = await statusCountsQuery();
      const scores: StatusCounts = data?.statusCounts;

      setStatusCounts(scores);
      const invalidCount = scores.invalidCount;
      const missingStatusCount = scores.missingStatusCount;
      const invalidEventAttributes = validateEventAttributes();

      setInvalidEventAttributesCount(invalidEventAttributes);
      setInvalidScoresCount(invalidCount);
      setMissingStatusCount(missingStatusCount);

      if (invalidCount > 0 || invalidEventAttributes > 0 || missingStatusCount > 0) {
        setFinalizeScoresErrorDialogOpen(true);
      } else {
        setFinalizeScoresDialogOpen(true);
      }
    } catch (error) {
      setErrorMessage("Error fetching registrants for finalization");
    }
  };

  const handleDownloadPATWorksheet = async () => {
    try {
      const { data } = await generateWorksheetMutation({
        variables: { id: eventData?.id }
      });
      const { success, message, url } = data?.patGenerateWorksheet ?? {};

      if (success) {
        window.open(url, "_blank", "noopener,noreferrer");
        trackEvent(
          EVENT_ANALYTICS_EVENTS.worksheetDownloaded,
          getEventAnalyticsData({
            source: "results",
            eventId: eventData?.id,
            eventSlug,
            action: "download"
          })
        );
      } else {
        showErrorSnackbar(message);
      }
    } catch (error) {
      setErrorMessage("Error generating worksheet");
    }
  };

  const handleConfirmFinalize = async () => {
    try {
      if (eventData === undefined) throw new Error("Undefined Event");

      setFinalizeScoresLoading(true);
      const {
        data: {
          patFinalizeScores: { success, message }
        }
      } = await patFinalizeScores({
        variables: {
          id: eventData.id
        },
        refetchQueries: [
          {
            query: eventDetailQuery,
            variables: { slug: eventSlug }
          },
          {
            query: EVENT_REGISTRANTS_QUERY,
            variables: { ...variables, ...paginationVariables, status: RegistrationStatus.REGISTERED }
          }
        ]
      });

      if (success) {
        showSnackbar("Scores finalized");
        trackEvent(
          EVENT_ANALYTICS_EVENTS.scoresFinalized,
          getEventAnalyticsData({
            source: "results",
            eventId: eventData.id,
            eventSlug,
            action: "finalize"
          })
        );
        await refetchLateCancelRegistrants();
      } else {
        showErrorSnackbar(`Unable to finalize scores: ${message}`);
      }
    } catch (error) {
      setErrorMessage("Error finalizing scores");
    } finally {
      setFinalizeScoresLoading(false);
      setFinalizeScoresDialogOpen(false);
    }
  };

  const handleBulkAttendanceUpdate = async (present: boolean) => {
    const selectedEventRegIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);

    if (selectedEventRegIds.length === 0) return;

    const originalRegistrationsSnapshot = [...registrations];
    const originalSelectedRowsStates: Record<string, RegistrantRow> = {};

    const attendancesForMutation = selectedEventRegIds
      .map((eventRegId) => {
        const reg = originalRegistrationsSnapshot.find((r) => r.id === eventRegId);
        if (reg && reg.sessionRegistrationId) {
          originalSelectedRowsStates[eventRegId] = { ...reg };
          return {
            eventSessionRegistrationId: reg.sessionRegistrationId,
            attended: present
          };
        }
        return null;
      })
      .filter(Boolean) as { eventSessionRegistrationId: string; attended: boolean }[];

    if (attendancesForMutation.length === 0) return;

    setRegistrations((prevRegistrations) =>
      prevRegistrations.map((reg) => {
        if (selectedEventRegIds.includes(reg.id)) {
          return { ...reg, attended: present };
        }
        return reg;
      })
    );

    try {
      const response = await updateEventSessionsRegistrationAttendances({
        variables: {
          attendances: attendancesForMutation
        },
        refetchQueries: [
          {
            query: EVENT_REGISTRANTS_QUERY,
            variables: { ...variables, ...paginationVariables, status: RegistrationStatus.REGISTERED }
          }
        ]
      });

      if (!response.data?.updateEventSessionsRegistrationAttendances?.success) {
        setRegistrations(originalRegistrationsSnapshot);
        showErrorSnackbar(
          response.data?.updateEventSessionsRegistrationAttendances?.message || "Failed to update bulk attendance."
        );
      }
    } catch (error) {
      setRegistrations(originalRegistrationsSnapshot);
      showErrorSnackbar("Failed to update bulk attendance.");
    }
  };

  const handleAttendanceUpdate = (sessionRegId: string, value: string | null) => {
    const attendedValue = value === "true" ? true : value === "false" ? false : null;

    const originalRegistrationsSnapshot = [...registrations];
    let originalRowState: RegistrantRow | undefined;

    setRegistrations((prevRegistrations) =>
      prevRegistrations.map((reg) => {
        if (reg.sessionRegistrationId === sessionRegId) {
          originalRowState = { ...reg };
          return { ...reg, attended: attendedValue, attendance: attendedValue ?? false };
        }
        return reg;
      })
    );

    updateEventSessionsRegistrationAttendances({
      variables: {
        attendances: [
          {
            eventSessionRegistrationId: sessionRegId,
            attended: attendedValue
          }
        ]
      },
      refetchQueries: [
        {
          query: EVENT_REGISTRANTS_QUERY,
          variables: { ...variables, ...paginationVariables, status: RegistrationStatus.REGISTERED }
        }
      ]
    })
      .then((response) => {
        if (!response.data?.updateEventSessionsRegistrationAttendances?.success) {
          if (originalRowState) {
            const rowToRevert = originalRowState;
            setRegistrations((prevRegistrations) =>
              prevRegistrations.map((r) => (r.sessionRegistrationId === sessionRegId ? rowToRevert : r))
            );
          } else {
            setRegistrations(originalRegistrationsSnapshot);
          }
          showErrorSnackbar(
            response.data?.updateEventSessionsRegistrationAttendances?.message || "Failed to update attendance."
          );
        }
      })
      .catch(() => {
        if (originalRowState) {
          const rowToRevert = originalRowState;
          setRegistrations((prevRegistrations) =>
            prevRegistrations.map((r) => (r.sessionRegistrationId === sessionRegId ? rowToRevert : r))
          );
        } else {
          setRegistrations(originalRegistrationsSnapshot);
        }
        showErrorSnackbar("Failed to update attendance.");
      });
  };

  const getFieldValue = (field: string, value: string): number | string | null => {
    if (field === "roundOneScore" || field === "roundTwoScore") {
      return value === "" ? null : Number(value);
    }
    return value;
  };

  const handleScoreUpdate = (field: string, id: string, value: string) => {
    patUpsertSubmission({
      variables: {
        input: {
          eventRegistrationId: id,
          [field]: getFieldValue(field, value)
        }
      },
      refetchQueries: [
        {
          query: EVENT_REGISTRANTS_QUERY,
          variables: { ...variables, ...paginationVariables, status: RegistrationStatus.REGISTERED }
        }
      ]
    }).then((response) => {
      if (!response.data?.patUpsertSubmission?.success) {
        const errorMessage = response.data?.patUpsertSubmission?.message || "Failed to update score.";
        const scoreField = field === "roundOneScore" ? "score_18_1" : "score_18_2";
        setRegistrations((prevRegistrations) =>
          prevRegistrations.map((reg) => (reg.id === id ? { ...reg, [scoreField]: "" } : reg))
        );
        setForceUpdate((prev) => prev + 1);
        showErrorSnackbar(errorMessage);
      } else {
        showSnackbar("Score updated");
        trackEvent(
          EVENT_ANALYTICS_EVENTS.scoresUpdated,
          getEventAnalyticsData({
            source: "results",
            eventId: eventData?.id,
            eventSlug,
            registrationId: id,
            action: "update",
            field
          })
        );
      }
    });
  };
  const handleStatusUpdate = (status: string | null, note: string | null, id: string | null) => {
    patUpsertSubmission({
      variables: {
        input: {
          eventRegistrationId: id,
          additionalStatus: status,
          note: note
        }
      },
      refetchQueries: [
        {
          query: EVENT_REGISTRANTS_QUERY,
          variables: { ...variables, ...paginationVariables, status: RegistrationStatus.REGISTERED }
        }
      ]
    }).then((response) => {
      if (!response.data?.patUpsertSubmission?.success) {
        setErrorMessage(response.data?.patUpsertSubmission?.message || "Failed to update status.");
      }
    });
  };

  const columns = getColumns({
    hasRegistrations,
    setMenu,
    setUpdateStatusDialogOpen,
    onAttendanceUpdate: handleAttendanceUpdate,
    onScoreUpdate: handleScoreUpdate,
    setTargetEventRegistration: setTargetEventRegistration,
    handleStatusUpdate: handleStatusUpdate,
    isMobile,
    isFinalized,
    canEdit,
    setShowEditScoreModal,
    isSetupComplete,
    setSelectedPgaId,
    setIsPersonSideSheetOpen
  });

  const baseBodyText = hasRegistrations
    ? "Please enter the attendance and player scores from this event. Once you have finished entering the scores, press 'Finalize Scores' to save them to the participants' official records."
    : "Registrants will be listed here when available.";

  const warningMessage = "Please complete the PAT Setup before entering scores.";

  return (
    <>
      <Menu />
      {!isSetupComplete && (
        <div className="w-full pt-8">
          <Banner
            description={warningMessage}
            variant="info"
            inline={true}
            closable={false}
            testId="pat-setup-warning"
          />
        </div>
      )}
      <TableDetails
        heading="PAT Scores"
        body={baseBodyText}
        ButtonComps={[
          () => (
            <Button
              testId="download-worksheet-button"
              variant="text"
              icon="download"
              onClick={() => handleDownloadPATWorksheet()}
            >
              Download PAT Worksheet
            </Button>
          ),
          ...(!isFinalized && hasRegistrations
            ? [
                () => {
                  const isWeatherEventWithoutPermission = hasWeatherEvents && !hasPermission(PERMISSION.editPAT);
                  const isDisabled = allRegistrantsLoading || isWeatherEventWithoutPermission;

                  const button = (
                    <Button
                      testId="finalize-scores-button"
                      onClick={isDisabled ? undefined : () => handleFinalizeScores()}
                      disabled={isDisabled}
                    >
                      Finalize Scores
                    </Button>
                  );

                  return isWeatherEventWithoutPermission ? (
                    <Tooltip
                      supportingText='PAT events with a "Weather Event" status must be finalized by the PGA of America Membership Team.  Please contact Membership at 800-474-2776 for assistance.'
                      variant="plain"
                      color="black"
                    >
                      {button}
                    </Tooltip>
                  ) : (
                    button
                  );
                }
              ]
            : [])
        ]}
      />
      <Table
        key={forceUpdate}
        data={registrations}
        loading={queryLoading || allRegistrantsLoading || lateCancelQueryLoading}
        renderHeader={
          registrations.length > 0 || searchValue !== ""
            ? (table: TableComponent<RegistrantRow>) => (
                <>
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-col gap-4 md:flex-row md:justify-left md:items-center w-full md:pr-4 lg:pr-0">
                      <div className="flex flex-col gap-2 md:flex-row md:gap-4 w-full md:w-auto">
                        <div className="w-full md:max-w-[260px]">
                          <Controller
                            name="search"
                            control={control}
                            render={({ field }) => (
                              <SearchInput
                                placeholder="Search Registrants"
                                query={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                  setVariables((prev) => ({
                                    ...prev,
                                    search: value
                                  }));
                                }}
                              />
                            )}
                          />
                        </div>
                        {filterActive && (
                          <div
                            className="flex items-center justify-start md:justify-center"
                            data-testid="clear-all-button"
                          >
                            <Button
                              variant="text"
                              onClick={() => {
                                resetPagination();
                                setVariables({ eventSlug });
                                reset({ search: "" });
                              }}
                            >
                              Clear All
                            </Button>
                          </div>
                        )}
                      </div>
                      {Object.keys(rowSelection).length > 0 && !isFinalized && (
                        <div className="flex flex-col sm:flex-row gap-4 sm:h-[40px] w-full md:w-auto justify-center md:justify-start items-start">
                          <Button
                            icon="person_check"
                            variant="outlined"
                            onClick={() => handleBulkAttendanceUpdate(true)}
                          >
                            Mark as Present
                          </Button>
                          <Button
                            icon="person_cancel"
                            variant="outlined"
                            onClick={() => handleBulkAttendanceUpdate(false)}
                          >
                            Mark as Absent
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="hidden ml-3 lg:block">
                      {renderFooter(registrantsData?.eventRegistrants, {
                        pageCount: registrations.length,
                        variant: "compact"
                      })()}
                    </div>
                  </div>
                  {rowSelected > 0 && !isFinalized && (
                    <div className="flex items-center gap-2 mt-4 flex-wrap md:flex-nowrap">
                      <Chip
                        onClick={() => {
                          setIsSelectedOnly(!isSelectedOnly);
                          registrantsQuery({
                            variables: {
                              ...variables,
                              ids: Object.keys(rowSelection)
                            }
                          });
                        }}
                        label="Show Selected Only"
                        selected={isSelectedOnly}
                      />
                      <Button
                        variant="text"
                        onClick={() => {
                          setRowSelection({});
                          setIsSelectedOnly(false);
                          resetPagination();
                        }}
                      >
                        Deselect All {rowSelected} {pluralize(rowSelected, "Row")}
                      </Button>
                    </div>
                  )}
                  <div
                    className={`flex items-center gap-2 ${
                      isFinalized ? "justify-center" : "justify-between"
                    } lg:hidden mt-3`}
                  >
                    <div className={`${isFinalized ? "invisible" : ""}`}>
                      <Checkbox
                        name="select-all"
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={(checked) => table.toggleAllRowsSelected(checked)}
                      />
                    </div>
                    {renderFooter(registrantsData?.eventRegistrants, {
                      pageCount: registrations.length,
                      variant: "compact"
                    })()}
                  </div>
                </>
              )
            : undefined
        }
        columns={columns}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Registrants" caption="This event does not have any registrants." iconName="person" />
        )}
        renderFooter={
          registrations.length > 0
            ? renderFooter(isSelectedOnly ? allRegistrantsData?.eventRegistrants : registrantsData?.eventRegistrants, {
                pageCount: registrations.length
              })
            : undefined
        }
        sortingState={sorting}
        onSortingChange={setSorting}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
      />
      <FinalizeScoresConfirmationModal
        open={finalizeScoresDialogOpen}
        onClose={() => setFinalizeScoresDialogOpen(false)}
        onSave={handleConfirmFinalize}
        loading={finalizeScoresLoading}
        scores={statusCounts}
        eventId={eventData?.id ?? ""}
      />
      <FinalizeScoresErrorDialog
        open={finalizeScoresErrorDialogOpen}
        onClose={() => setFinalizeScoresErrorDialogOpen(false)}
        missingRegistrantScores={invalidScoresCount}
        missingEventInformation={invalidEventAttributesCount}
        missingStatus={missingStatusCount}
      />
      {updateStatusDialogOpen && targetEventRegistration && (
        <UpdateStatusModal
          onClose={() => setUpdateStatusDialogOpen(false)}
          eventRegistration={targetEventRegistration}
          onSave={(status, note) => handleStatusUpdate(status, note, targetEventRegistration.id)}
        />
      )}
      {showEditScoreModal && targetEventRegistration && (
        <EditScoreModal
          onClose={() => {
            setShowEditScoreModal(false);
            refetchRegistrants({ ...variables, ...paginationVariables, status: RegistrationStatus.REGISTERED });
          }}
          submission={targetEventRegistration}
        />
      )}
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => setIsPersonSideSheetOpen(false)}
        pgaId={selectedPgaId}
      />
    </>
  );
}
